import { useEffect, useRef } from "react";
import { Box, Dialog, IconButton, Typography } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloseIcon from "@mui/icons-material/Close";

export interface LightboxPhoto {
    src: string;
    alt: string;
    title: string;
    subtitle?: string;
}

interface Props {
    open: boolean;
    photos: LightboxPhoto[];
    index: number;
    onIndexChange: (index: number) => void;
    onClose: () => void;
}

const SWIPE_PX = 40;

/** Foto a schermo intero su sfondo nero: frecce, tasti ← → , swipe e didascalia */
export default function PhotoLightbox({ open, photos, index, onIndexChange, onClose }: Props) {
    const touchStartX = useRef<number | null>(null);
    const count = photos.length;
    const go = (delta: number) => onIndexChange((index + delta + count) % count);
    const goRef = useRef(go);
    goRef.current = go;

    useEffect(() => {
        if (!open) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "ArrowLeft") goRef.current(-1);
            if (e.key === "ArrowRight") goRef.current(1);
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [open]);

    // precarica le foto adiacenti
    useEffect(() => {
        if (!open || count < 2) return;
        [1, -1].forEach((d) => { new Image().src = photos[(index + d + count) % count].src; });
    }, [open, index, photos, count]);

    if (count === 0) return null;
    const photo = photos[Math.min(index, count - 1)];
    const navSx = { position: "absolute", top: "50%", transform: "translateY(-50%)", color: "white", bgcolor: "rgba(255,255,255,0.15)" } as const;

    return (
        <Dialog fullScreen open={open} onClose={onClose} PaperProps={{ sx: { bgcolor: "#000", color: "white" } }}>
            <Box
                sx={{ position: "relative", flex: 1, display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden" }}
                onTouchStart={(e) => { touchStartX.current = e.touches[0].clientX; }}
                onTouchEnd={(e) => {
                    if (touchStartX.current === null) return;
                    const dx = e.changedTouches[0].clientX - touchStartX.current;
                    touchStartX.current = null;
                    if (Math.abs(dx) > SWIPE_PX) go(dx < 0 ? 1 : -1);
                }}
            >
                <Box
                    key={photo.src}
                    component="img"
                    src={photo.src}
                    alt={photo.alt}
                    sx={{
                        maxWidth: "100%", maxHeight: "100%", objectFit: "contain", animation: "lightboxFade 0.4s ease",
                        "@keyframes lightboxFade": { from: { opacity: 0 }, to: { opacity: 1 } },
                    }}
                />
                <IconButton aria-label="Chiudi" onClick={onClose} sx={{ position: "absolute", top: 12, right: 12, color: "white", bgcolor: "rgba(255,255,255,0.15)" }}>
                    <CloseIcon />
                </IconButton>
                {count > 1 && (
                    <>
                        <IconButton aria-label="Foto precedente" onClick={() => go(-1)} sx={{ ...navSx, left: 12 }}>
                            <ChevronLeftIcon fontSize="large" />
                        </IconButton>
                        <IconButton aria-label="Foto successiva" onClick={() => go(1)} sx={{ ...navSx, right: 12 }}>
                            <ChevronRightIcon fontSize="large" />
                        </IconButton>
                    </>
                )}
                <Box sx={{ position: "absolute", bottom: 0, left: 0, right: 0, p: 2, textAlign: "center", background: "linear-gradient(transparent, rgba(0,0,0,0.75))" }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, textTransform: "capitalize" }}>{photo.title}</Typography>
                    {photo.subtitle && (
                        <Typography variant="body2" sx={{ opacity: 0.8 }}>
                            {photo.subtitle}{count > 1 ? ` · ${index + 1}/${count}` : ""}
                        </Typography>
                    )}
                </Box>
            </Box>
        </Dialog>
    );
}
